import { FC, MouseEventHandler } from "react";
import { Styled } from "../types";
import { capitalizeAssociatedPress } from "../../../helpers/string/capitalize";
import { Titled } from "../../../components/types";
import { ButtonType, CommonControlsProps } from "./types";
import { DEFAULT_BUTTON_STYLES, SECONDARY_BUTTON_STYLES } from "./styles";

const primaryButtonStyles =
  "text-white bg-black hover:bg-stone-900 dark:text-black dark:bg-white dark:hover:bg-stone-100 transition-colors";
const tetriaryButtonStyles =
  "text-stone-700 dark:text-white bg-stone-50 dark:bg-stone-700 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors";
const disabledButtonStyles =
  "text-stone-400 bg-stone-200 dark:bg-stone-700 transition-colors";
const dangerButtonStyles =
  "text-red-500 hover:text-white border border-red-500 dark:border-red-700 hover:bg-red-600 transition-colors";
const ghostButtonStyles =
  "text-black dark:text-white underline focus:ring-0 hover:underline hover:text-stone-900 dark:hover:text-stone-100 transition-colors";
const activeButtonStyles =
  "text-white dark:text-black bg-stone-900 dark:bg-stone-100 hover:bg-stone-800 hover:dark:bg-white";
const inactiveButtonStyles =
  "text-stone-900 dark:text-white bg-stone-200 dark:bg-stone-700 hover:bg-stone-300 dark:hover:bg-stone-600";

const buttonTypeColors: Record<ButtonType, string> = {
  primary: primaryButtonStyles,
  secondary: SECONDARY_BUTTON_STYLES,
  tetriary: tetriaryButtonStyles,
  disabled: disabledButtonStyles,
  danger: dangerButtonStyles,
  ghost: ghostButtonStyles,
  active: activeButtonStyles,
  inactive: inactiveButtonStyles,
  iconOnly: "",
  custom: "",
};

type ButtonAttrs = React.ButtonHTMLAttributes<HTMLButtonElement>;

const buttonTextStyles = {
  hidden: "sr-only",
  lgOnly: "hidden sm:inline",
  show: "",
};

type ButtonTextStyles = keyof typeof buttonTextStyles;

type ButtonTypes = CommonControlsProps & {
  buttonType?: ButtonType;
  refs?: React.MutableRefObject<null>;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  textStyle?: ButtonTextStyles;
  tooltip?: string;
};

export type ButtonProps = Titled & ButtonTypes & ButtonAttrs & Styled;

export const Button: FC<React.PropsWithChildren<ButtonProps>> = ({
  buttonType = "primary",
  onClick,
  title,
  name,
  children,
  className,
  textStyle = "lgOnly",
  capitalize = true,
  customId,
  tooltip,
  ...args
}) => {
  const shadowStyles = buttonType !== "ghost" ? "shadow-none" : "";
  const generalStyle =
    buttonType === "custom" || buttonType === "iconOnly"
      ? ""
      : DEFAULT_BUTTON_STYLES;
  const colors = buttonTypeColors[args.disabled ? "disabled" : buttonType];
  const relevantTextStyle =
    buttonType === "iconOnly" || textStyle === "hidden"
      ? "hidden"
      : !!children
        ? textStyle
        : "show";

  return (
    <button
      className={`${generalStyle} ${colors} cursor-pointer disabled:cursor-not-allowed ${className ? className : ""} ${
        shadowStyles
      }`}
      key={buttonType}
      onClick={onClick}
      title={tooltip || name}
      id={customId}
      {...args}
      type={args?.type ?? "button"}
    >
      {children}
      <span className={buttonTextStyles[relevantTextStyle]}>
        {capitalize ? capitalizeAssociatedPress(title) : title}
      </span>
      {tooltip && <span className="sr-only">{tooltip}</span>}
    </button>
  );
};
